import { FC, useState } from 'react';

import { useTranslation } from 'next-i18next/pages';

import { Button, Form, Input, message } from 'antd';
import cn from 'classnames';

import { leads } from '@/api/amo-crm/leads';
import { Environment } from '@/constants/environments';
import { useUserState } from '@/context/user.context';
import { TranslationNamespace } from '@/i18n';
import { LocalStorage } from '@/utils';
import { getCurrentSessionUtm } from '@/utils/extractUtm';
import { CloseOutlined } from '@ant-design/icons';

import { PhoneInput } from '../common';
import { PersonalDataConfirm } from '../PersonalDataConfirm';

import { getSourceForRequestDemoCRM } from './utils';

import styles from './RequestDemoPopup.module.scss';

type Props = {
	isShownPopup: boolean;
	onCancel: () => void;
};

type FormType = {
	name: string;
	phone: string;
	company: string;
};

export const OPEN_DEMO_WINDOW_TIMER = 'openDemoWindowTimer';
export const REQUESTED_DEMO = 'requestDemo';

export const RequestDemoPopup: FC<Props> = ({ isShownPopup, onCancel }) => {
	const [form] = Form.useForm<FormType>();
	const [isLoading, setIsLoading] = useState(false);
	const [confirm, setConfirm] = useState(false);
	const { t } = useTranslation(TranslationNamespace.InviteDemoModal);
	const { user } = useUserState();

	const onSubmit = async () => {
		try {
			await form.validateFields();
		} catch {
			return;
		}

		setIsLoading(true);
		try {
			const { name, company, phone } = form.getFieldsValue();
			const utm = getCurrentSessionUtm(window.location.href);
			const errors = await leads({
				company,
				phone: `+${phone}`,
				name,
				source: getSourceForRequestDemoCRM(process.env.ENVIRONMENT as Environment),
				...utm,
			});

			if (errors) {
				let text = '';
				Object.values(errors).forEach((v) => {
					text += `${v.join('\n')}\n`;
				});

				message.error(text);
				return;
			}

			LocalStorage?.setItem(REQUESTED_DEMO, 'true');
			onCancel();
		} catch {
			message.error('Ошибка при отправке данных. Попробуйте еще раз.');
		} finally {
			setIsLoading(false);
		}
	};

	if (!isShownPopup) {
		return;
	}

	return (
		!user && (
			<div
				className={cn(styles.container, {
					[styles.demoContainer]: process.env.ENVIRONMENT === Environment.Demo,
				})}
			>
				<Button type="text" className={styles.closeIcon} onClick={onCancel}>
					<CloseOutlined />
				</Button>
				<div className={styles.title}>{t('invite-demo-modal-title')}</div>
				<div className={styles.description}>{t('invite-demo-modal-description')}</div>
				<Form form={form} className={styles.form}>
					<Form.Item name="name">
						<Input maxLength={100} size="large" placeholder="ФИО" />
					</Form.Item>
					<Form.Item
						name="phone"
						rules={[
							{ required: true, message: 'Обязательное поле' },
							{ len: 11, message: 'Проверьте корректность ввода номера' },
						]}
					>
						<PhoneInput sizeType="large" />
					</Form.Item>
					<Form.Item name="company">
						<Input maxLength={100} size="large" placeholder="Название компании" />
					</Form.Item>
				</Form>
				<PersonalDataConfirm checked={confirm} onCheckedChange={setConfirm} />
				<Button
					id="demo_btn"
					type="primary"
					className={styles.button}
					block
					size="middle"
					disabled={!confirm}
					loading={isLoading}
					onClick={onSubmit}
				>
					{t('invite-demo-modal-button')}
				</Button>
			</div>
		)
	);
};
